import { createInstance, type i18n as I18nInstance } from 'i18next'
import Backend from 'i18next-http-backend'
import { initReactI18next } from 'react-i18next'

export const defaultNS = 'common'
export const plantAssessmentNS = 'plantAssessment'
export const spectrumNS = 'spectrum'
export const companiesNS = 'companies'
export const araGenerationNS = 'araGeneration'
export const matrixNS = 'matrix'
export const namespaces = [
  defaultNS,
  plantAssessmentNS,
  spectrumNS,
  companiesNS,
  araGenerationNS,
  matrixNS
] as const

export const supportedLngs = ['en', 'pt-BR'] as const
export type SupportedLanguage = (typeof supportedLngs)[number]

/** localStorage key for the user's chosen app language, persisted across visits. */
export const LANGUAGE_STORAGE_KEY = 'scope:language'

function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return value === 'en' || value === 'pt-BR'
}

/**
 * Resolve the initial app language, in priority order:
 * 1. a previously stored choice under `scope:language`;
 * 2. the browser's `navigator.language`, mapped to a supported language;
 * 3. fallback `en`.
 */
export function resolveInitialLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') return 'en'

  const stored = window.localStorage?.getItem(LANGUAGE_STORAGE_KEY)
  if (isSupportedLanguage(stored)) return stored

  const browser = window.navigator?.language
  if (browser) {
    if (browser.toLowerCase().startsWith('pt')) return 'pt-BR'
    if (browser.toLowerCase().startsWith('en')) return 'en'
  }

  return 'en'
}

/** Apply a new language: switch the i18n instance and persist the choice. */
export function changeLanguage(instance: I18nInstance, next: SupportedLanguage) {
  void instance.changeLanguage(next)
  if (typeof window !== 'undefined') {
    window.localStorage?.setItem(LANGUAGE_STORAGE_KEY, next)
  }
}

export function createI18n(): I18nInstance {
  const instance = createInstance()

  void instance
    .use(Backend)
    .use(initReactI18next)
    .init({
      lng: resolveInitialLanguage(),
      fallbackLng: 'en',
      supportedLngs: [...supportedLngs],
      defaultNS,
      ns: [...namespaces],
      interpolation: { escapeValue: false },
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
        requestOptions: { cache: 'no-cache' }
      }
    })

  return instance
}
